import CommonConstants from "@/utils/constants/CommonConstants.ts";
import Translator from "@/utils/common/Translator.ts";
import ShortCutsUtil from "@/utils/common/ShortCutsUtil.ts";
import ActionHandler from "@/utils/moreMenu/ActionHandler.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import type {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import OwnerType from "@/utils/constants/OwnerType.ts";
import PreferencesRequest from "@/utils/constants/PreferencesRequest.ts";
import MetaData from "@/utils/common/MetaData.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {Dialog} from "quasar";
import SimpleEntityDialog from "@/components/dialogs/SimpleEntityDialog.vue";
import GenTranslationsDialog from "@/components/dialogs/GenTranslationsDialog.vue";
import SimpleEntities from "@/utils/constants/SimpleEntities.ts";
import DTOCriteria = namaTypes.DTOCriteria;
import FetchPreferencesRequest = namaTypes.FetchPreferencesRequest;
import ListPageMatchingRequest = namaTypes.ListPageMatchingRequest;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import GUIActionPlaceholder = namaTypes.GUIActionPlaceholder;
import DTOApprovalCase = namaTypes.DTOApprovalCase;

export default class MoreMenuItem {
  protected _text?: string;
  protected _id?: string;
  private _seperator: boolean = false;
  private _executeFn?: Function;
  private _customization?: namaTypes.CustomizedGUIActionInfo;
  private _searchValue: string = '';
  private _dependentOnReplicationOptions = false;
   actionInfo?: GUIActionPlaceholder;
  constructor() {
  }

  static createFromActionHandler(handler: ActionHandler): MoreMenuItem {
    let item = new MoreMenuItem();
    item.id = handler.actionInfo.id;
    item.actionInfo = handler.actionInfo;
    if (handler.actionInfo.customization && handler.actionInfo.customization.securityID)
      item.id = handler.actionInfo.customization.securityID;
    item.seperator = ObjectChecker.isAnyEqualToFirst(handler.actionInfo.id, CommonConstants.SEPARATOR, CommonConstants.MORE_MENU_SEPERATOR);
    if (item.seperator)
      return item;
    item.searchValue += item.id + " " + Translator.translateId(item.id) + " " + Translator.translateIdInOtherLang(item.id) + " ";
    item.text = Translator.translateTitledId(handler.idPrefix, handler.actionInfo);
    if (item.text) {
      item.searchValue += item.text + " ";
      item.searchValue += Translator.translateTitledIdInOtherLang(handler.idPrefix, handler.actionInfo) + " ";
      item.text = ShortCutsUtil.addShortCutToTitle(item.text, handler.actionInfo.id!)
    }
    item.executeFn = async () => await handler.performAction();
    return item;
  }

  static createViewVersions(version: number, presenter: EditPresenter, current: boolean | undefined): MoreMenuItem {
    let item = new MoreMenuItem();
    item.id = "viewVersion" + version;
    let text = current ? Translator.translateId("currentVersion") :
      Translator.translateId("viewVersion").replace("{0}", version + "");
    let otherText = current ? Translator.translateIdInOtherLang("currentVersion") :
      Translator.translateIdInOtherLang("viewVersion").replace("{0}", version + "");
    item.searchValue += item.id + " " + text + " " + otherText + " ";
    item.text = text;
    item.executeFn = async () => await presenter.viewVersion(current ? undefined : version);
    return item;
  }

  static createShowSpecificVersionMenu(presenter: EditPresenter): MoreMenuItem {
    let item = new MoreMenuItem();
    item.id = "sepecificVersion";
    let text = Translator.translateId(item.id);
    let otherText = Translator.translateIdInOtherLang(item.id);
    item.searchValue += item.id + " " + text + " " + otherText + " ";
    item.text = text;
    item.executeFn = async () => await presenter.showSpecificVersionDialog();
    return item;
  }

  static createSeperatorMenuItem(): MoreMenuItem {
    let menuItem = new MoreMenuItem();
    menuItem.seperator = true;
    return menuItem;
  }

  get text(): string | undefined {
    return this._text;
  }

  set text(value: string | undefined) {
    this._text = value;
  }

  get id(): string | undefined {
    return this._id;
  }

  set id(value: string | undefined) {
    this._id = value;
  }

  get seperator(): boolean {
    return this._seperator;
  }

  set seperator(value: boolean) {
    this._seperator = value;
  }

  get executeFn(): Function | undefined {
    return this._executeFn;
  }

  set executeFn(value: Function) {
    this._executeFn = value;
  }

  get customization(): namaTypes.CustomizedGUIActionInfo | undefined {
    return this._customization;
  }

  set customization(value: namaTypes.CustomizedGUIActionInfo | undefined) {
    this._customization = value;
  }

  get searchValue(): string {
    return this._searchValue;
  }

  set searchValue(value: string) {
    this._searchValue = value;
  }

  set dependentOnReplicationOptions(value: boolean) {
    this._dependentOnReplicationOptions = value;
  }

  get dependentOnReplicationOptions(): boolean {
    return this._dependentOnReplicationOptions;
  }

  async callGUIAction(action: MoreMenuItem, metaData: namaTypes.NaMaMetaData) {
    if (action.executeFn) {
      await action.executeFn();
      return;
    }
    if (!action.customization)
      action.customization = {};
    if (action.customization.guiPostActionsId) {
      // runCustomGuiPostAction(that.actionInfo, presenter);
      // return
    }
    console.log("clicked");
    console.log(action);
  }

  static createMoreMenuItem(id: string, executeFn: () => void): MoreMenuItem {
    let menuItem = new MoreMenuItem();
    menuItem.text = Translator.translateId(id);
    menuItem.id = id;
    menuItem.executeFn = executeFn;
    menuItem.searchValue = menuItem.id + " " + Translator.translateId(menuItem.id) + " " + Translator.translateIdInOtherLang(menuItem.id);
    return menuItem;
  }

  static createShowFieldIdsMenuItem() {
    let item = this.createMoreMenuItem("showFieldIds", () => {
      useNamaFlagsStore().toggleShowIds()
    });
    item.dependentOnReplicationOptions = true;
    return item;
  }

  static createExportTranslationsMenuItem(presenter: EditPresenter) {
    return this.createMoreMenuItem("exportTranslations", async () => {
      const {default: EditViewUtils} = await import("@/utils/editView/EditViewUtils.ts");
      const translations = new EditViewUtils(presenter).getTranslations();
      Dialog.create({
        component: GenTranslationsDialog,
        componentProps: {
          translations,
          presenter: () => presenter
        }
      })
    });
  }

  static createShowFieldInfoMenuItem() {
    let item = this.createMoreMenuItem("showFieldInfo", () => {
      useNamaFlagsStore().activateShowFieldInfo()
    });
    item.dependentOnReplicationOptions = true;
    return item;
  }

  static createOpenEntityDMMenuItem(entityType: string) {
    let item = this.createMoreMenuItem("showEntityDM", () => {
      window.open("https://dm.namasoft.com#" + entityType, "_blank", "");
    });
    item.dependentOnReplicationOptions = true;
    return item;
  }
}

export class SimpleEntityMenuItem extends MoreMenuItem
{
  private readonly _entityType?: string;
  private readonly _criteria?: DTOCriteria;
  private readonly _object?: BaseEntityDTO;

  constructor(titleId: string, subtitleId: string, entityType: string, criteria: DTOCriteria | undefined, object?: Record<string, any>) {
    super();
    this.text = Translator.translateId(titleId);
    this.searchValue = Translator.translateId(titleId) + " " + Translator.translateIdInOtherLang(titleId);
    if (subtitleId) {
      this.text += ": " + Translator.translateId(subtitleId);
      this.searchValue += ": " + Translator.translateId(subtitleId) + " " + Translator.translateIdInOtherLang(subtitleId);
    }
    this.id = titleId + subtitleId;
    this._entityType = entityType;
    this._criteria = criteria;
    this._object = object;
  }

  static fromCriteria(titleId: string, subtitleId: string, entityType: string, criteria: DTOCriteria): SimpleEntityMenuItem {
    return new SimpleEntityMenuItem(titleId, subtitleId, entityType, criteria);
  }

  static fromObject(id: string, entityType: string, object: Record<string, any>): SimpleEntityMenuItem {
    return new SimpleEntityMenuItem(id, '', entityType, undefined, object);
  }

  override get executeFn(): Function | undefined {
    return async () => await this.showSimpleEntityPopup();
  }

  async showSimpleEntityPopup() {
    let request: FetchPreferencesRequest = {
      owner: {
        ownerType: OwnerType.Entity,
        ownerId: this._entityType
      },
      properties: [PreferencesRequest.EDIT_LAYOUT, PreferencesRequest.META_DATA]
    };
    await MetaData.fetchPreferences(request, async (result: Record<any, any> | null) => {
      if (!result)
        return;
      let record: BaseEntityDTO | undefined = this._object
      if (!record && this._criteria)
        record = await SimpleEntityMenuItem.fetchSimpleEntity(this._entityType ?? "", this._criteria);
      Dialog.create({
        component: SimpleEntityDialog,
        componentProps: {
          editLayout: result.editLayout,
          metaData: result.metaData,
          object: record,
          entityType: this._entityType,
          title: this.text
        }
      });
    });
  }

  static async showCurrentApprovalCasePopup(currentApprovalCase: DTOApprovalCase) {
    const {default: EditViewToolbarUtils} = await import("@/utils/editView/EditViewToolbarUtils.ts");
    const instance = new SimpleEntityMenuItem(ObjectChecker.toStringOrEmpty(EditViewToolbarUtils.getCurrentApprovalAction().id), "", SimpleEntities.ApprovalCase, undefined, currentApprovalCase);
    await instance.showSimpleEntityPopup();
  }

  static async fetchSimpleEntity(entityType: string, criteria: DTOCriteria) {
    let listPageMatchingRequest: ListPageMatchingRequest = {
      entityType: entityType,
      criteria: criteria,
      page: 0,
      pageSize: -1
    }
    let result: BaseEntityDTO[] = await NamaCommon.namaFetch(NamaURLS.ListPageMatching, {
      data: listPageMatchingRequest
    });
    if (result && result.length)
      return result[0];
  }
}
